import { useCallback, useMemo, useState } from 'react';

import { isRadiusInRange, RADIUS_RANGE_MESSAGE, type FieldCircle } from '@/lib/maps/circle';
import type { FieldTarget } from '@/lib/maps/hit-test';

import {
  parseCoordinate,
  parseRadius,
  rangeMessage,
  sanitizeCoordinate,
  sanitizeRadiusInput,
  type CoordinateAxis,
} from './coordinates';

/** The three cells a circle's draft has, as strings — a cell mid-typing is `'-'` or `'1.'`, and one
 *  the Grower has not reached is `''`, neither of which a number can hold. */
type CircleFields = { lat: string; lon: string; radius: string };

/** The inline message against each cell. An absent key means that cell is not in error. */
export type CircleErrors = Partial<Record<keyof CircleFields, string>>;

type Draft = { fields: CircleFields; errors: CircleErrors };

const EMPTY: CircleFields = { lat: '', lon: '', radius: '' };
/** A stable empty-errors reference — `draft?.errors ?? {}` would mint a fresh object on every clean
 *  render, which would make `canSave`'s `useMemo` recompute (and re-render) every time. */
const NO_ERRORS: CircleErrors = {};

/**
 * The cells a **committed** circle implies — what the panel shows whenever the draft is clean.
 *
 * **Full precision, `String()` — never `formatCoordinate`.** A cell is an editable value, not a
 * readout: an untouched Mid Point is re-plotted verbatim by the panel's own `Save`, so rounding it
 * would silently move a Mid Point the Grower placed by clicking. Same call `rowsFromPoints` makes,
 * for the same reason.
 */
const fieldsFromCircle = (circle: FieldCircle | null): CircleFields =>
  circle
    ? {
        lat: String(circle.midPoint.lat),
        lon: String(circle.midPoint.lon),
        radius: String(circle.radiusFt),
      }
    : EMPTY;

export type CircleDraftOptions = {
  /** The **committed** circle — what the canvas draws, or `null` for none. Read on every render: a
   *  clean draft is supposed to follow it, so a Mid Point dropped on the canvas, a resize by handle,
   *  an Undo and a delete all reach the panel with no wiring of their own. */
  circle: FieldCircle | null;
  /** The optimistic pre-filter's targets, exactly as `useFieldPoints` takes them. */
  existingFields: readonly FieldTarget[];
  /** `null` when creating, the Field's id when editing. Required, never defaulted. */
  excludeFieldId: string | null;
  /** `useFieldPoints().commitCircle` — the one way a valid draft becomes the committed shape, and
   *  one undo entry. Injected rather than reached for, so this hook needs no map state of its own. */
  commitCircle: (circle: FieldCircle) => Promise<boolean>;
};

/**
 * The Field Mapping panel's **draft coordinates** in circle **Shape mode** (113601) — a **Mid
 * Point** pair and a **Radius** in feet.
 *
 * The sibling of `useCoordinateDraft`, and it owns the same commit level: cells that may be empty
 * or invalid, that are **not** in the undo history, and that leave the committed shape alone until
 * this panel's own `Save`. `draft === null` means clean, which is what makes the panel derive from
 * the canvas while never overwriting a draft in progress.
 *
 * `existingFields` / `excludeFieldId` are held for `commitCircle`'s placement round trip rather than
 * used here: the panel's blur asks only the **local** questions (coordinate range, Radius range),
 * because a circle's placement verdict is about the whole disc and there is no disc until a Radius
 * has been typed too.
 */
export function useCircleDraft({ circle, commitCircle }: CircleDraftOptions) {
  const [draft, setDraft] = useState<Draft | null>(null);

  const committed = useMemo(() => fieldsFromCircle(circle), [circle]);
  const fields = draft?.fields ?? committed;
  const errors = draft?.errors ?? NO_ERRORS;

  /** Every mutation goes through here, so a draft can only ever be born by copying the committed
   *  cells — there is no path that starts from blank and loses the Grower's circle. */
  const edit = useCallback(
    (change: (draft: Draft) => Draft) =>
      setDraft((current) => change(current ?? { fields: committed, errors: {} })),
    [committed],
  );

  const setValue = useCallback(
    (axis: CoordinateAxis, raw: string) =>
      edit(({ fields: current, errors: currentErrors }) => ({
        fields: { ...current, [axis]: sanitizeCoordinate(raw) },
        // Typing is progress: this cell's own error goes, and only this cell's.
        errors: { ...currentErrors, [axis]: undefined },
      })),
    [edit],
  );

  const setRadius = useCallback(
    (raw: string) =>
      edit(({ fields: current, errors: currentErrors }) => ({
        fields: { ...current, radius: sanitizeRadiusInput(raw) },
        errors: { ...currentErrors, radius: undefined },
      })),
    [edit],
  );

  /** Blur on a Mid Point half. Range only, and an **empty** cell is deliberately left alone: blank
   *  is a save-time error, and flagging a cell on the Grower's way past it would be noise. */
  const blurCell = useCallback(
    (axis: CoordinateAxis) =>
      edit(({ fields: current, errors: currentErrors }) => {
        const value = current[axis];
        const invalid = value.trim() !== '' && parseCoordinate(value, axis) === null;
        return {
          fields: current,
          errors: { ...currentErrors, [axis]: invalid ? rangeMessage(axis) : undefined },
        };
      }),
    [edit],
  );

  /**
   * Blur on the **Radius**. The range is the client's own rule (see `isRadiusInRange`), so this is
   * the whole of "a Radius outside 1 to 26,400 ft shows the message and the circle is neither drawn
   * nor resized" — nothing is committed from a blur at all.
   */
  const blurRadius = useCallback(
    () =>
      edit(({ fields: current, errors: currentErrors }) => {
        const parsed = parseRadius(current.radius);
        const invalid =
          current.radius.trim() !== '' && (parsed === null || !isRadiusInRange(parsed));
        return {
          fields: current,
          errors: { ...currentErrors, radius: invalid ? RADIUS_RANGE_MESSAGE : undefined },
        };
      }),
    [edit],
  );

  /** The draft as a circle, or `null` if any cell is empty, out of range or unparseable.
   *  All-or-nothing: half a circle is not a thing the canvas can draw. */
  const candidate = useMemo((): FieldCircle | null => {
    const lat = parseCoordinate(fields.lat, 'lat');
    const lon = parseCoordinate(fields.lon, 'lon');
    const radiusFt = parseRadius(fields.radius);
    if (lat === null || lon === null || radiusFt === null || !isRadiusInRange(radiusFt))
      return null;
    return { midPoint: { lat, lon }, radiusFt };
  }, [fields]);

  /** 113601: Save stays disabled until BOTH a valid Radius and a valid Mid Point are present, and
   *  while anything is in error. Derived, so correcting a value re-enables it as the Grower types. */
  const canSave = useMemo(
    () => candidate !== null && Object.values(errors).every((message) => message === undefined),
    [candidate, errors],
  );

  /**
   * `Save`, pressed — enabled or not, because the control is `aria-disabled` and has to be able to
   * SAY that a blank is not valid (113601). An empty cell gets the very message an out-of-range one
   * gets, which is what the criterion asks for in as many words.
   */
  const attemptSave = useCallback(async () => {
    const blanks: CircleErrors = {};
    if (fields.lat.trim() === '') blanks.lat = rangeMessage('lat');
    if (fields.lon.trim() === '') blanks.lon = rangeMessage('lon');
    if (fields.radius.trim() === '') blanks.radius = RADIUS_RANGE_MESSAGE;

    if (blanks.lat || blanks.lon || blanks.radius) {
      edit(({ fields: current, errors: currentErrors }) => ({
        fields: current,
        errors: { ...currentErrors, ...blanks },
      }));
      return;
    }

    if (!canSave || !candidate) return;
    // Only a COMMITTED shape retires the draft. A refused placement leaves every cell where it was.
    if (await commitCircle(candidate)) setDraft(null);
  }, [fields, canSave, candidate, edit, commitCircle]);

  /** Cancel — confirmed by the caller, which owns the dialog. Back to `null` is back to mirroring
   *  the committed circle, which is exactly "the panel reverts with the shape unchanged". */
  const cancel = useCallback(() => setDraft(null), []);

  return {
    lat: fields.lat,
    lon: fields.lon,
    radius: fields.radius,
    errors,
    dirty: draft !== null,
    setValue,
    setRadius,
    blurCell,
    blurRadius,
    canSave,
    attemptSave,
    cancel,
  };
}
